import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * MonthYearPicker Component
 * A month and year picker matching the website's UI design
 */
const MonthYearPicker = ({
  value,
  onChange,
  minDate = '2023-12',
  maxDate = '2025-10',
  dataMaxDate = null, // NEW: Separate max date for data availability
  placeholder = 'Select month',
  label = 'Select Month'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const dropdownRef = useRef(null);

  // Parse min and max dates
  const [minYear, minMonth] = minDate.split('-').map(Number);
  const [maxYear, maxMonth] = maxDate.split('-').map(Number);
  
  // Parse data max date (for disabling months beyond data availability)
  const [dataMaxYear, dataMaxMonth] = dataMaxDate 
    ? dataMaxDate.split('-').map(Number)
    : [maxYear, maxMonth];

  // Initialize selected year and month from value
  useEffect(() => {
    if (value) {
      const [month, year] = value.split(' ');
      const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth();
      setSelectedYear(parseInt(year));
      setSelectedMonth(monthNum);
    } else {
      // Default to current year
      const currentYear = new Date().getFullYear();
      setSelectedYear(currentYear >= minYear && currentYear <= maxYear ? currentYear : maxYear);
    }
  }, [value, minYear, maxYear]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const fullMonthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleYearChange = (direction) => {
    const newYear = selectedYear + direction;
    if (newYear >= minYear && newYear <= maxYear) {
      setSelectedYear(newYear);
    }
  };

  const handleMonthClick = (monthIndex) => {
    // Check if month is disabled
    if (isMonthDisabled(monthIndex)) {
      return;
    }

    setSelectedMonth(monthIndex);
    const monthName = fullMonthNames[monthIndex];
    onChange(`${monthName} ${selectedYear}`);
    setIsOpen(false);
  };

  const isMonthDisabled = (monthIndex) => {
    // Disable if before minDate
    if (selectedYear === minYear && monthIndex < minMonth - 1) {
      return true;
    }
    
    // Disable if after maxDate (calendar range)
    if (selectedYear === maxYear && monthIndex > maxMonth - 1) {
      return true;
    }
    
    // Disable if after dataMaxDate (data availability)
    if (selectedYear === dataMaxYear && monthIndex > dataMaxMonth - 1) {
      return true;
    }
    
    // Disable if year is beyond dataMaxYear
    if (selectedYear > dataMaxYear) {
      return true;
    }
    
    return false;
  };

  const isMonthSelected = (monthIndex) => {
    if (!value) return false;
    const [month, year] = value.split(' ');
    const valueMonthIndex = new Date(Date.parse(month + " 1, 2000")).getMonth();
    return monthIndex === valueMonthIndex && selectedYear === parseInt(year);
  };

  const displayValue = value || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-slate-300 text-slate-700 text-sm rounded-lg focus:ring-1 focus:ring-teal-100 focus:border-teal-100 p-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span className={value ? 'text-slate-700 font-medium' : 'text-slate-400'}>
            {displayValue}
          </span>
        </div>
        <ChevronUp className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 z-50 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
          {/* Header with Year Selector */}
          <div className="bg-white p-3 flex items-center justify-center border-b border-slate-200">
            <button
              type="button"
              onClick={() => handleYearChange(-1)}
              disabled={selectedYear <= minYear}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronUp className="w-4 h-4 text-slate-600" />
            </button>
            <div className="text-xl font-bold text-slate-800 mx-8 min-w-[80px] text-center">
              {selectedYear}
            </div>
            <button
              type="button"
              onClick={() => handleYearChange(1)}
              disabled={selectedYear >= maxYear}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronDown className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          {/* Month Grid */}
          <div className="p-3 grid grid-cols-3 gap-2">
            {months.map((month, index) => {
              const disabled = isMonthDisabled(index);
              const selected = isMonthSelected(index);

              return (
                <button
                  key={month}
                  type="button"
                  onClick={() => handleMonthClick(index)}
                  disabled={disabled}
                  className={`
                    py-2.5 px-3 rounded-lg text-sm font-medium transition-all duration-150
                    ${selected
                      ? 'bg-teal-500 text-white shadow-md'
                      : disabled
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900'
                    }
                  `}
                >
                  {month}
                </button>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="bg-white px-3 py-2 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              Select a month to filter patient data
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
